import React, { useEffect, useMemo, useState, useRef, useImperativeHandle, forwardRef } from 'react'
import "./GetInvoice.css"
import CardChevron from '@/Components/Common/CardChevron';
import { onActivateKey } from '@/Components/OnwardTravel/onActivateKey';
import { useIntl } from 'react-intl';
import { Col, Form, Input, Row, Select } from 'antd';
import { useRecState } from '../../../../Orchestrator/Recoils/Events/Recoil';
import { _eventBus } from '../../../../core/events/EventBus';
import { CountryCodesType } from '../../../../Orchestrator/Types/Types/Country/CountryCodesType';
import { IUserResponse } from '../../../../Orchestrator/Types/Index';
import FlagImage from '../../../Shared/FlagImage';
import RedCaretIcon from '../../../Shared/RedCaretIcon';
import CountrySearchPopup from '../../../Common/Inputs/CountrySearchPopup';
import { matchesCountryTerm } from '../../../../Lib/Utils/countryFilter';
import { INVOICE_ADDRESS_REGEX, INVOICE_ADDRESS_REGEX_OPTIONAL } from '../../../../Lib/Utils/invoiceAddress';
import { checkoutTree } from '../../../../Orchestrator/ConstantTree/CheckoutPage/CheckoutTree';
const { Option } = Select;

export interface GetInvoiceRef {
  validateInvoice: () => Promise<boolean>;
  getInvoiceData: () => {
    companyName?: string;
    trn?: string;
    address1?: string;
    address2?: string;
    city?: string;
    countryBilling?: string;
  };
}

const GetInvoice = forwardRef<GetInvoiceRef>((_props, ref) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [localInvoiceCountrySearch, setLocalInvoiceCountrySearch] = useState("");
  const [countryCodeData] = useRecState<CountryCodesType[]>("countryCodesAtom");
  const userData = useRecState<IUserResponse>("userDataAtom");
  const invoiceInitialized = useRef(false);

  useEffect(() => {
    setTimeout(() => {
      _eventBus.emit({
        eventName: checkoutTree.Invoice.Init
      });
    }, 100);
  }, [])

  // Pre-fill invoice data from user data if available
  useEffect(() => {
    if (userData[0]?.invoicingAddress && !invoiceInitialized.current) {
      const { name, trn, street, complement, city, countryCode } = userData[0].invoicingAddress;
      if (name) {
        setIsOpen(true);
        form.setFieldsValue({
          companyName: name,
          trn: trn,
          address1: street,
          address2: complement,
          city: city,
          countryBilling: countryCode,
        });
        invoiceInitialized.current = true;
      }
    }
  }, [userData, form]);

  const uniqueCountryCodes: CountryCodesType[] = useMemo(() => {
    let codes: any = {}
    countryCodeData.forEach((c) => { codes[c.countryCode] = c })
    return Object.values(codes)
  }, [countryCodeData]);

  const filteredInvoiceCountryData = useMemo(
    () => uniqueCountryCodes.filter((c) => matchesCountryTerm(c, localInvoiceCountrySearch)),
    [uniqueCountryCodes, localInvoiceCountrySearch],
  );

  const toggleOpen = () => {
    if (isOpen) {
      setSummaryText(getSummary());
    }
    setIsOpen(!isOpen);
    if (!isOpen) {
      // When opening, set default country to UAE if no country is selected
      const currentCountry = form.getFieldValue("countryBilling");
      if (!currentCountry) {
        form.setFieldsValue({
          countryBilling: "AE"
        });
      }
    }
  }

  const getSummary = () => {
    if (!isOpen) return summaryText;
    const companyName = form.getFieldValue("companyName");
    const address1 = form.getFieldValue("address1");
    const city = form.getFieldValue("city");
    const country = form.getFieldValue("countryBilling");

    if (companyName && address1 && city && country) {
      const countryName = filteredInvoiceCountryData.find(c => c.value === country)?.countryNameEn || country;
      return `${companyName}, ${address1}, ${city}, ${countryName}`;
    }
    return "";
  };

  const handleValuesChange = () => {
    // Trigger validation to show/hide required messages dynamically
    setTimeout(() => {
      form.validateFields().catch(() => {
        // Ignore validation errors, we just want to trigger the validation display
      });
    }, 100);
  };

  // Dynamic validator for required fields when invoice data exists
  const requiredWhenHasData = (message: string) => ({
    validator: (_: any, value: any) => {
      const hasData = hasInvoiceData();
      if (hasData && (!value || !value.trim())) {
        return Promise.reject(new Error(message));
      }
      return Promise.resolve();
    }
  });

  // Check if any invoice fields (except countryBilling and trn) have values
  const hasInvoiceData = () => {
    const values = form.getFieldsValue();
    const { companyName, address1, address2, city } = values;
    return !!(companyName?.trim() || address1?.trim() || address2?.trim() || city?.trim());
  };

  // Validate invoice form
  const validateInvoice = async (): Promise<boolean> => {
    if (!hasInvoiceData()) {
      return true;
    }

    try {
      // If any invoice data exists, validate required fields
      await form.validateFields(['companyName', 'address1', 'address2', 'city']);

      // Also validate TRN if it has a value (for pattern validation)
      const trnValue = form.getFieldValue('trn');
      if (trnValue && trnValue.trim()) {
        await form.validateFields(['trn']);
      }

      return true;
    } catch (error) {
      console.error('Invoice validation failed:', error);
      return false;
    }
  };

  const getInvoiceData = () => {
    return form.getFieldsValue();
  };

  useImperativeHandle(ref, () => ({
    validateInvoice,
    getInvoiceData
  }));

  return (
    <div className="invoice-card">
      <div
        className='invoice-toggle'
        onClick={toggleOpen}
        onKeyDown={onActivateKey(toggleOpen)}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <div className='toggle-left'>
          <span className='invoice-heading'>
            {intl.formatMessage({ id: "wantReceipt" })}
          </span>
          {getSummary() != "" && !isOpen && <div className='invoice-subheading'>{getSummary()}</div>}
        </div>
        {isOpen ? (
          <CardChevron direction="up" />
        ) : (
          <CardChevron direction="down" />
        )}
      </div>

      {isOpen && (
        <div className="invoice-body">
          <Form
            form={form}
            name="personal_information"
            layout="vertical"
            // onFinish={handleSave}
            onValuesChange={handleValuesChange}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  required={false}
                  name="companyName"
                  label={intl.formatMessage({ id: "companyNameLabel" })}
                  rules={[
                    requiredWhenHasData(intl.formatMessage({ id: "invoiceCompanyRequired" })),
                    {
                      pattern: /^[A-Za-z0-9\s&'-]{2,50}$/,
                      message: intl.formatMessage({ id: "invoiceCompanyPattern" }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "companyNamePlaceholder",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  required={false}
                  name="trn"
                  label={intl.formatMessage({ id: "trn" })}
                  rules={[
                    { required: false, message: intl.formatMessage({ id: "invoiceTrnRequired" }) },
                    {
                      pattern: /^[a-zA-Z0-9]*$/,
                      message: intl.formatMessage({ id: "invoiceTrnPattern" }),
                    },
                  ]}
                >
                  <Input placeholder={intl.formatMessage({ id: "trnPlaceholder" })} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  required={false}
                  name="address1"
                  label={intl.formatMessage({ id: "addressLine1" })}
                  rules={[
                    requiredWhenHasData(intl.formatMessage({ id: "invoiceAddressRequired" })),
                    {
                      min: 5,
                      message: intl.formatMessage({ id: "invoiceAddressMin" }),
                    },
                    {
                      max: 50,
                      message: intl.formatMessage({ id: "invoiceAddressMax" }),
                    },
                    {
                      pattern: INVOICE_ADDRESS_REGEX,
                      message: intl.formatMessage({ id: "invoiceAddressPattern" }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "addressLine1Placeholder",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="address2"
                  required={false}
                  label={intl.formatMessage({ id: "addressLine2" })}
                  rules={[
                    requiredWhenHasData(intl.formatMessage({ id: "invoiceAddressRequired" })),
                    {
                      min: 5,
                      message: intl.formatMessage({ id: "invoiceAddressMin" }),
                    },
                    {
                      max: 50,
                      message: intl.formatMessage({ id: "invoiceAddressMax" }),
                    },
                    {
                      pattern: INVOICE_ADDRESS_REGEX_OPTIONAL,
                      message: intl.formatMessage({ id: "invoiceAddressPattern" }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "addressLine2Placeholder",
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  required={false}
                  name="city"
                  label={intl.formatMessage({ id: "city" })}
                  rules={[
                    requiredWhenHasData(intl.formatMessage({ id: "invoiceCityRequired" })),
                    {
                      pattern: /^[A-Za-z\s-]{1,100}$/,
                      message: intl.formatMessage({ id: "invoiceCityPattern" }),
                    },
                  ]}
                >
                  <Input placeholder={intl.formatMessage({ id: "cityPlaceholderPI" })} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  required={false}
                  name="countryBilling"
                  label={intl.formatMessage({ id: "country" })}
                  rules={[{ required: false, message: intl.formatMessage({ id: "invoiceCountryRequired" }) }]}
                >
                  <Select
                    className="select-country"
                    suffixIcon={<RedCaretIcon />}
                    placeholder={<span>{intl.formatMessage({ id: "selectYourCountry" })}</span>}
                    onOpenChange={(open) => {
                      if (!open) setLocalInvoiceCountrySearch("");
                    }}
                    popupRender={(menu) => (
                      <CountrySearchPopup
                        value={localInvoiceCountrySearch}
                        onChange={setLocalInvoiceCountrySearch}
                        placeholder={intl.formatMessage({ id: "searchCountryOrCode" })}
                      >
                        {menu}
                      </CountrySearchPopup>
                    )}
                  >
                    {filteredInvoiceCountryData.map((country) => (
                      <Option
                        key={country.value + country.label}
                        value={country.value}
                        className="mobile-country-code-option"
                      >
                        <div className="d-flex gap-10 align-items-center">
                          <FlagImage countryCode={country.countryCode} />
                          {" " + country.countryNameEn}
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </div>
  );
});

GetInvoice.displayName = 'GetInvoice';

export default GetInvoice